import clsx from 'clsx';
import type { Transition, Variants } from 'framer-motion';
import { AnimatePresence, motion } from 'framer-motion';
import { lazy, Suspense, useEffect } from 'react';

import { Button } from '../../mobile-ds';
import { Cross } from '../../retail-ds/icons';
import CloseFilled from '../icons/CloseFilled';

const Modal = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.Modal,
  }))
);

const ModalOverlay = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.ModalOverlay,
  }))
);

const ModalPortal = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.ModalPortal,
  }))
);

const ModalContent = lazy(() =>
  import('ui/retail-ds/Modal').then((mod) => ({
    default: mod.ModalContent,
  }))
);

const MotionModalContent = motion(ModalContent, {
  forwardMotionProps: true,
});

const motionVisible: Variants = {
  hidden: {
    opacity: 0,
    top: '100%',
  },
  visible: {
    opacity: 1,
    top: 0,
  },
};

const spring: Transition = {
  delay: 0.1,
  type: 'spring',
  stiffness: 100,
  damping: 15,
  mass: 0.5,
};

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  secondaryCTAText?: string;
  primaryCTA?: JSX.Element;
  zIndex?: number;
  containerClassName?: string;
  isMWeb?: boolean | null;
  headerText: string;
}

const FullPageModal = ({
  isOpen,
  onClose,
  secondaryCTAText = 'Press ESC to close',
  primaryCTA,
  children,
  zIndex,
  containerClassName,
  isMWeb = false,
  headerText,
}: React.PropsWithChildren<IProps>) => {
  // for keyboard a11y
  useEffect(() => {
    function close(this: Window, e: KeyboardEvent) {
      if (e.key === 'Escape') {
        onClose();
      }
    }
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  return (
    <Suspense fallback={null}>
      <Modal modal open onOpenChange={onClose}>
        <AnimatePresence>
          {isOpen && (
            <ModalPortal>
              <ModalOverlay
                className="bg-none"
                {...(zIndex && { style: { zIndex } })}
              />
              <MotionModalContent
                key="modal"
                className="no-scrollbar h-[100dvh] w-screen rounded-none bg-transparent p-0"
                {...(zIndex && { style: { zIndex } })}
              >
                <motion.section
                  className={clsx({
                    'bg-secondary-50 relative z-10 flex h-full w-full flex-col overflow-auto':
                      true,
                    'bg-white': isMWeb,
                  })}
                  variants={motionVisible}
                  initial="hidden"
                  animate="visible"
                  exit="hidden"
                  transition={spring}
                >
                  {!isMWeb ? (
                    <motion.header
                      // className={styles['modal-header']}
                      className="border-secondary-200 bg-secondary-50 sticky top-0 z-10 flex w-full items-center justify-between border-b-[1px]"
                      style={{
                        padding: isMWeb ? 24 : '24px 64px',
                      }}
                    >
                      <Button
                        intent="tertiary"
                        className="h-12 min-w-fit text-base font-semibold leading-[120%] text-gray-400 focus-visible:border-none"
                        onClick={() => {
                          onClose();
                        }}
                      >
                        <CloseFilled />
                        {isMWeb ? 'Close' : secondaryCTAText}
                      </Button>
                      {primaryCTA}
                    </motion.header>
                  ) : (
                    <motion.header className="sticky top-0 z-10 flex w-full flex-row items-center justify-between bg-white px-4">
                      <p className="text-retail-text-main flex-1 text-center text-sm">
                        {headerText}
                      </p>
                      <Button
                        intent="tertiary"
                        className="min-w-0 focus-visible:border-white active:bg-white"
                        onClick={() => {
                          onClose();
                        }}
                      >
                        <Cross width={18} height={18} />
                      </Button>
                    </motion.header>
                  )}
                  <motion.div
                    className={containerClassName}
                    // className={styles['modal-body']}
                  >
                    {/* @ts-ignore */}
                    {children}
                  </motion.div>
                </motion.section>
              </MotionModalContent>
            </ModalPortal>
          )}
        </AnimatePresence>
      </Modal>
    </Suspense>
  );
};

export default FullPageModal;
